import * as React from "react";
import {observer} from "mobx-react";
import {useNavigate} from "react-router-dom";
import {Box, LinearProgress, MenuItem, Select, Typography} from "@mui/material";
import {useApplication} from "../../../ApplicationProvider";
import {useNavigatorFeature} from "../../../useFeature";
import LayoutContentWrapper from "../../../components/layout/LayoutContentWrapper";
import LayoutContentTitle from "../../../components/layout/LayoutContentTitle";
import FilledButton from "../../../components/buttons/FilledButton";
import LeadAssignmentQueueFeature from "./LeadAssignmentQueueFeature";
import {styles} from "./LeadAssignmentQueueStyles";

function LeadAssignmentQueueScreen() {
    const application = useApplication();
    const navigator = useNavigate();
    const style = styles();
    const feature = useNavigatorFeature(() => new LeadAssignmentQueueFeature(application), navigator);
    const isFeatureReady = feature && feature.loaded;

    const ageOf = (lead) => {
        const millisecondsPerMinute = 60000;
        const minutes = Math.floor((Date.now() - lead.creationDatetime.getTime()) / millisecondsPerMinute);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);
        if (days > 0) return `${days} ${days === 1 ? 'día' : 'días'}`;
        if (hours > 0) return `${hours} h`;
        return `${minutes} min`;
    };

    const assigneeLabelOf = (assignee) => (assignee.isTheAssigner ? `${assignee.name} (yo)` : assignee.name);

    const activeLeadsLabelOf = (activeLeadsCount) => (
        activeLeadsCount === 1 ? '1 activo' : `${activeLeadsCount} activos`
    );

    const closerWorkloadCard = (closerWorkload) => (
        <Box key={closerWorkload.closerEmail} sx={style.workloadCard}>
            <Typography noWrap title={closerWorkload.closerName} sx={style.workloadName}>
                {closerWorkload.closerName}
            </Typography>
            <Typography sx={style.workloadCount}>{activeLeadsLabelOf(closerWorkload.activeLeadsCount)}</Typography>
            <LinearProgress
                variant="determinate"
                value={(closerWorkload.activeLeadsCount / feature.highestActiveLeadsCount) * 100}
                sx={style.workloadBar}
            />
        </Box>
    );

    const unassignedLeadRow = (lead) => (
        <Box key={lead.id} sx={style.leadRow}>
            <Box sx={style.leadSummary}>
                <Typography sx={style.leadName}>{lead.fantasyName}</Typography>
                <Typography sx={style.leadDetail}>{lead.originChannel} · {ageOf(lead)}</Typography>
            </Box>
            <Select
                size="small"
                value={feature.chosenResponsibleEmailFor(lead)}
                onChange={(event) => feature.chooseResponsibleFor(lead, event.target.value)}
                sx={style.responsibleSelect}
            >
                {feature.assignees.map((assignee) => (
                    <MenuItem key={assignee.email} value={assignee.email}>
                        {assigneeLabelOf(assignee)}
                    </MenuItem>
                ))}
            </Select>
            <FilledButton onClick={() => feature.assignLead(lead)}>Asignar</FilledButton>
        </Box>
    );

    const content = () => (
        <Box sx={style.container}>
            <LayoutContentTitle title="Asignación de leads"/>
            <Box sx={style.workloadGrid}>
                {feature.closersWorkloads.map(closerWorkloadCard)}
            </Box>
            <Box sx={style.queue}>
                {feature.hasUnassignedLeads
                    ? feature.unassignedLeads.map(unassignedLeadRow)
                    : <Typography sx={style.emptyQueue}>No hay leads pendientes de asignar</Typography>}
            </Box>
        </Box>
    );

    return <LayoutContentWrapper isLoading={!isFeatureReady} renderContent={() => content()}/>;
}

export default observer(LeadAssignmentQueueScreen);
